import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

interface PaginatedContent {
  queryKey: string;
  queryFunction: (params: any) => any;
  initialData?: FindResponse<any>;
}

const usePaginatedContent = ({
  queryKey,
  queryFunction,
  initialData,
}: PaginatedContent) => {
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    pageSize: 10,
    pageCount: 1,
    total: 0,
  });

  const sort = ["createdAt:desc"];

  const {
    isSuccess,
    data: responseData,
    isLoading,
    isError,
    isFetching,
  } = useQuery<FindResponse<any>>(
    [queryKey, pagination, sort],
    () =>
      queryFunction({
        key: queryKey,
        pagination,
        sort,
      }),
    {
      initialData,
    }
  );

  const onNext = () => {
    if (responseData) {
      const { page, pageCount } = responseData.meta.pagination;
      setPagination({
        ...pagination,
        page: Math.min(page + 1, pageCount),
      });
      scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const onBack = () => {
    const { page } = pagination;
    setPagination({
      ...pagination,
      page: Math.max(page - 1, 1),
    });
    scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const onSetPage = (value: number) => {
    setPagination({
      ...pagination,
      page: value,
    });
    scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (responseData) {
      const { pagination } = responseData.meta;
      setPagination({
        ...pagination,
        pageCount: pagination.pageCount,
        total: pagination.total,
      });
    }
  }, [responseData]);

  return {
    isSuccess,
    isLoading,
    isError,
    isFetching,
    data: responseData?.data || [],
    pagination,
    onNext,
    onBack,
    onSetPage,
  };
};

export default usePaginatedContent;
